import { queryNoTracking } from '../../../services/DataService';
import { t } from '../../../services/HelperService';
import { FIXED_PICKLISTS_TEMPLATE_GROUP } from './fixedPicklistLiteralCatalog';

const safeString = (value) => (value ?? '').toString();

const q_AdvancedQuery_Lookup = queryNoTracking('AdvancedQuery_Lookup', 0);

/**
 * Template catalog groups for the advanced-filter LOOKUPS a filter schema exposes —
 * the generalisation of the Products group (productCatalog.js) to every LookupKey:
 * reps, suppliers, custom statuses, fault reasons, job types, stages... Each group
 * carries `lookupKey` so the assistant dialog can map its items to the prompt form
 * "Name (<lookupKey> <id>)" (see toPromptLookupReference), which the server-side
 * lookupValues skill and the host validator recognise as a pre-resolved reference.
 *
 * The groups are derived from a filter schema the host ALREADY exposes to this user
 * (for commissions, Commissions_GetFilterSchema), so no new data surface is opened;
 * hosts must not spread lookup groups beyond the data their assistant can reach.
 */

/**
 * Walks a filter schema ({ Entities: [{ ID, Fields: [{ ID, Prompt, Type, LookupKey,
 * LookupKeyType }] }] }) and returns the distinct lookup keys with the translated
 * label of the FIRST field that uses each (supplier-scoped LookupKeyType fields are
 * skipped — they need a supplier context the prompt does not have). A client-built
 * schema may carry an already-translated `Label` on a field instead of a `Prompt`.
 */
export const collectSchemaLookupKeys = (filterSchema, fieldStringBase = 'application_strings.entities') => {
  const result = new Map();
  const walk = (entity) => {
    if (!entity) return;
    for (const field of (Array.isArray(entity.Fields) ? entity.Fields : [])) {
      const key = safeString(field?.LookupKey).trim();
      if (!key || safeString(field?.LookupKeyType).trim()) continue;
      if (result.has(key)) continue;
      const explicit = safeString(field?.Label).trim();
      const prompt = safeString(field?.Prompt).trim();
      const label = explicit || (prompt ? t(`${fieldStringBase}.${prompt}`) : key);
      result.set(key, label);
    }
    for (const child of (Array.isArray(entity.Entities) ? entity.Entities : [])) walk(child);
  };
  for (const entity of (Array.isArray(filterSchema?.Entities) ? filterSchema.Entities : [])) walk(entity);
  return result;
};

/**
 * A lookup is a FIXED PICKLIST (the server's FiltersHelper.StaticValues: job types, PO
 * statuses, stages...) when every row's ID is its Text - the stored code doubles as the
 * display key that t() translates. Data-backed lookups (suppliers, reps, custom
 * statuses) carry a numeric/guid ID that never equals the name.
 */
export const isFixedPicklistRows = (rows) => Array.isArray(rows)
  && rows.length > 0
  && rows.every((r) => safeString(r?.id).trim() && safeString(r?.id).trim() === safeString(r?.text).trim());

/**
 * Nests the fixed-picklist lookup groups under one "Fixed Picklists" root ("Fixed
 * Picklists -> Job Type -> Sales") so codes do not sit beside data-backed entities in
 * the menu. The root uses the FixedPicklists group NAME the per-editor literal mappers
 * key on (OData / SQL / JavaScript string literal, prompt reference) and a translated
 * `label` for display; each nested sub-group keeps its own lookupKey.
 */
export const nestFixedPicklistGroups = (groups, rootLabel = t('Fixed Picklists')) => {
  const list = (Array.isArray(groups) ? groups : []).filter((g) => g);
  const fixed = list.filter((g) => g.fixedPicklist);
  if (fixed.length === 0) return list;
  const rest = list.filter((g) => !g.fixedPicklist);
  return [
    ...rest,
    {
      group: FIXED_PICKLISTS_TEMPLATE_GROUP,
      label: rootLabel,
      items: fixed.map((g) => ({ label: g.group, lookupKey: g.lookupKey, items: g.items })),
    },
  ];
};

/**
 * Fetches one template catalog group per lookup key of the schema (parallel,
 * best-effort: a failing or empty lookup simply yields no group). Items carry the
 * row's Text as label and its ID as token/snippet. Product rows come from the same
 * endpoint but the caller normally keeps the dedicated Products group instead
 * (pass excludeKeys: ['product']) so existing prompt wording stays unchanged.
 * Fixed-picklist groups are nested under one FixedPicklists root unless
 * nestFixedPicklists is false.
 */
export const fetchLookupCatalogGroups = async (filterSchema, { excludeKeys = [], maxItemsPerGroup = 500, nestFixedPicklists = true } = {}) => {
  const keys = collectSchemaLookupKeys(filterSchema);
  const excluded = new Set((excludeKeys ?? []).map((k) => safeString(k).toLowerCase()));
  const wanted = [...keys.entries()].filter(([key]) => !excluded.has(key.toLowerCase()));
  if (wanted.length === 0) return [];

  const groups = await Promise.all(wanted.map(async ([key, label]) => {
    try {
      const rows = await q_AdvancedQuery_Lookup.getAllWithCache({ lookupKey: key });
      const pairs = (Array.isArray(rows) ? rows : [])
        .map((r) => ({ id: safeString(r?.ID).trim(), text: safeString(r?.Text).trim() }))
        .filter((r) => r.id && r.text)
        .slice(0, maxItemsPerGroup);
      const items = pairs
        // Static picklists return translation keys as Text (the filter dropdown
        // translates them too); real names pass through t() unchanged.
        .map((r) => ({ label: t(r.text), token: r.id, snippet: r.id }));
      if (items.length === 0) return null;
      return { group: label || key, lookupKey: key, fixedPicklist: isFixedPicklistRows(pairs), items };
    } catch {
      return null;
    }
  }));

  const present = groups.filter((g) => g);
  return nestFixedPicklists ? nestFixedPicklistGroups(present) : present;
};

/**
 * Prompt-editor mapper for a lookup group's items: inserts "Name (<lookupKey> <id>)"
 * so the request stays human-readable while carrying the exact id. The server's
 * lookupValues prompt section tells the assistant such references are pre-resolved.
 */
export const toPromptLookupReference = (lookupKey) => (value, item) => {
  const id = safeString(value).trim().replace(/^["']|["']$/g, '');
  const name = safeString(item?.label).trim();
  const key = safeString(lookupKey).trim();
  return name ? `${name} (${key} ${id})` : `${key} ${id}`;
};

/** True when a catalog node, or any node beneath it, carries a lookupKey. */
export const hasLookupReferenceGroups = (catalog) => {
  const carries = (node) => !!node
    && (!!safeString(node.lookupKey).trim() || (Array.isArray(node.items) && node.items.some(carries)));
  return (Array.isArray(catalog) ? catalog : []).some(carries);
};

/**
 * Maps every catalog node carrying a lookupKey through toPromptLookupReference. The key
 * may sit on the group itself (supplier, rep) or on a nested sub-group (the FixedPicklists
 * root holds one sub-group per picklist); leaves inherit the nearest key above them.
 */
export const mapLookupGroupsForPrompt = (catalog) => {
  const mapNode = (node, inheritedKey) => {
    if (!node) return node;
    const key = safeString(node.lookupKey).trim() || inheritedKey;
    if (Array.isArray(node.items)) {
      return key || node.items.some((c) => safeString(c?.lookupKey).trim() || Array.isArray(c?.items))
        ? { ...node, items: node.items.map((c) => mapNode(c, key)) }
        : node;
    }
    if (!key) return node;
    const value = safeString(node.snippet || node.token);
    if (!value) return node;
    const literal = toPromptLookupReference(key)(value, node);
    return { ...node, token: literal, snippet: literal };
  };
  return (Array.isArray(catalog) ? catalog : []).map((group) => mapNode(group, ''));
};
